import {
  Component,
  ChangeDetectionStrategy,
  computed,
  inject,
  input,
  model,
  output,
  signal,
  effect,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzMessageService } from 'ng-zorro-antd/message';
import { EmpleadoService } from '@services/empleado.service';
import { VacacionesService } from '@services/vacaciones.service';
import { CatalogoItem, EmpleadoList } from '@schemas/empleado.schema';
import { DirectorAsignacion } from '@schemas/vacaciones.schema';
import { describirAdscripcion } from '@shared/adscripcion';
import { PageHeader } from '@intelishs-group/ui';

/**
 * Modal para cambiar área y dirección de un empleado. Lo abre la acción
 * "Cambiar Área" del dropdown en la lista de empleados.
 *
 * Es un cambio sin histórico: el backend hace DELETE+INSERT sobre
 * rh.relempleadodireccionarea (PK compuesta) y devuelve el empleado ya con
 * los valores nuevos. No hay confirmación intermedia — Guardar dispara.
 */
@Component({
  selector: 'app-cambiar-area-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, NzButtonModule, NzModalModule, NzSelectModule, PageHeader],
  templateUrl: './cambiar-area-modal.html',
  styleUrl: './cambiar-area-modal.css',
})
export class CambiarAreaModal {
  private readonly empleadoService = inject(EmpleadoService);
  private readonly vacacionesService = inject(VacacionesService);
  private readonly message = inject(NzMessageService);
  private readonly destroyRef = inject(DestroyRef);

  visible = model(false);
  empleado = input<EmpleadoList | null>(null);

  /**
   * El contexto va en el SUBTÍTULO, nunca en el título, y se arma aquí porque
   * `page-header` recibe un string: con partes opcionales, el encadenado se
   * lee peor en el template.
   */
  subtitulo = computed(() => {
    const emp = this.empleado();
    return emp ? `${emp.idInterno} · ${emp.nombreCompleto}` : '';
  });
  guardado = output<void>();

  areas = signal<CatalogoItem[]>([]);
  direcciones = signal<CatalogoItem[]>([]);
  directores = signal<DirectorAsignacion[]>([]);
  loadingCats = signal(false);
  guardando = signal(false);

  // Selección actual del modal
  areaIdSel = signal<number | null>(null);
  direccionIdSel = signal<number | null>(null);
  directorIdSel = signal<number | null>(null);

  /**
   * La combinación con la que se abrió el modal. Se guarda aparte de la
   * selección porque los tres dropdowns arrancan preseleccionados con ella:
   * sin este registro no hay forma de saber si el usuario cambió algo.
   */
  private readonly originales = signal<{
    areaId: number | null;
    direccionId: number | null;
    directorId: number | null;
  } | null>(null);

  /** Nombre del director vigente, para el panel de referencia. */
  readonly directorOriginal = computed(() => {
    const id = this.originales()?.directorId;
    if (id == null) return '';
    const d = this.directores().find((x) => x.directorId === id);
    return d ? `${d.directorNombre} (${d.directorIdInterno})` : '';
  });

  readonly seleccionCompleta = computed(
    () => this.areaIdSel() != null && this.direccionIdSel() != null && this.directorIdSel() != null,
  );

  readonly hayCambios = computed(() => {
    const o = this.originales();
    if (!o) return false;
    return (
      this.areaIdSel() !== o.areaId ||
      this.direccionIdSel() !== o.direccionId ||
      this.directorIdSel() !== o.directorId
    );
  });

  describirAdscripcion = describirAdscripcion;

  constructor() {
    // Al abrir el modal: reset selección + cargar (catálogos + directores +
    // detalle del empleado en paralelo) + preseleccionar los IDs actuales.
    effect(() => {
      if (!this.visible()) return;

      this.areaIdSel.set(null);
      this.direccionIdSel.set(null);
      this.directorIdSel.set(null);
      this.originales.set(null);
      this.cargarDatosIniciales();
    });
  }

  /**
   * Carga 3 fuentes en paralelo:
   *   - getCatalogos: áreas y direcciones.
   *   - getDirectores: lista completa de directores.
   *   - getById(emp.id): empleado completo, para leer superiorId actual.
   *
   * Cuando todo llega, preselecciona los 3 dropdowns con la combinación
   * vigente para que el usuario solo cambie lo que necesite cambiar.
   */
  private cargarDatosIniciales(): void {
    const emp = this.empleado();
    if (!emp) return;

    this.loadingCats.set(true);
    forkJoin({
      cat: this.empleadoService.getCatalogos(),
      dirs: this.vacacionesService.getDirectores(),
      det: this.empleadoService.getById(emp.id),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ cat, dirs, det }) => {
          this.areas.set(cat.areas);
          this.direcciones.set(cat.direcciones);
          this.directores.set(dirs);

          // Preselección por descripción para área/dirección (el listado solo
          // tiene la descripción, no el ID).
          const areaActual = cat.areas.find((a) => a.descripcion === emp.area);
          const dirActual = cat.direcciones.find((d) => d.descripcion === emp.direccion);
          if (areaActual) this.areaIdSel.set(areaActual.id);
          if (dirActual) this.direccionIdSel.set(dirActual.id);

          // El director sí viene como ID del detalle (response.superiorId).
          if (det.superiorId != null) {
            this.directorIdSel.set(det.superiorId);
          }

          this.originales.set({
            areaId: this.areaIdSel(),
            direccionId: this.direccionIdSel(),
            directorId: this.directorIdSel(),
          });

          this.loadingCats.set(false);
        },
        error: () => {
          this.message.error('No se pudieron cargar los datos del empleado');
          this.loadingCats.set(false);
        },
      });
  }

  cerrar(): void {
    this.visible.set(false);
  }

  guardar(): void {
    const emp = this.empleado();
    const areaId = this.areaIdSel();
    const direccionId = this.direccionIdSel();
    const directorId = this.directorIdSel();
    if (!emp || areaId == null || direccionId == null || directorId == null) {
      this.message.warning('Selecciona área, dirección y director');
      return;
    }

    this.guardando.set(true);
    this.empleadoService
      .changeArea(emp.id, areaId, direccionId, directorId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          // Invalida la caché de directores: como acabamos de mover al
          // empleado a otro SuperiorId, el director nuevo debe verlo en
          // "Mis Autorizaciones" sin necesidad de refrescar la sesión.
          // El backend re-calcula directorEmpleadoIds en cada llamada;
          // solo había que romper el shareReplay del frontend.
          this.vacacionesService.invalidateDirectoresCache();

          this.message.success('Área y director actualizados');
          this.guardando.set(false);
          this.guardado.emit();
          this.cerrar();
        },
        error: (err: unknown) => {
          const fallback = 'Error al cambiar el área';
          const msg = this.extraerMensajeError(err, fallback);
          this.message.error(msg);
          this.guardando.set(false);
        },
      });
  }

  private extraerMensajeError(err: unknown, fallback: string): string {
    if (err && typeof err === 'object' && 'error' in err) {
      const body = (err as { error?: { errorMessage?: string } }).error;
      if (body?.errorMessage) return body.errorMessage;
    }
    return fallback;
  }
}
